import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import StarRating from './StarRating';
import { reviewsAPI } from '../services/api';
import { formatDate as formatDateUtil } from '../utils/dateFormat';
import '../styles/SellerReviews.css';

const SellerReviews = ({ sellerId }) => {
  const { t } = useTranslation();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    fetchReviews();
  }, [sellerId, page]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await reviewsAPI.getSellerReviews(sellerId, page, 5);
      setReviews(response.data.reviews);
      setStats(response.data.stats);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Erreur chargement avis:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return formatDateUtil(dateString);
  };

  if (loading && page === 1) {
    return <div className="reviews-loading">{t('reviews.loading')}</div>;
  }

  if (!stats || stats.total_reviews === 0) {
    return (
      <div className="no-reviews">
        <p>{t('reviews.noReviews')}</p>
        <span>{t('reviews.noReviewsHint')}</span>
      </div>
    );
  }

  const ratingPercentage = (count) => {
    return stats.total_reviews > 0 ? (count / stats.total_reviews) * 100 : 0;
  };

  return (
    <div className="seller-reviews">
      {/* Résumé des notes */}
      <div className="reviews-summary">
        <div className="average-rating">
          <span className="rating-value">
            {parseFloat(stats.average_rating).toFixed(1)}
          </span>
          <StarRating rating={parseFloat(stats.average_rating)} size="large" />
          <span className="total-reviews">{t('reviews.count', { count: stats.total_reviews })}</span>
        </div>

        <div className="rating-breakdown">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = stats[`${['one', 'two', 'three', 'four', 'five'][star - 1]}_star${star > 1 ? 's' : ''}`] || 0;
            return (
              <div key={star} className="rating-bar">
                <span className="star-label">{star} ★</span>
                <div className="bar-container">
                  <div
                    className="bar-fill"
                    style={{ width: `${ratingPercentage(count)}%` }}
                  />
                </div>
                <span className="count">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Liste des avis */}
      <div className="reviews-list">
        {reviews.map((review) => (
          <div key={review.id} className="review-card">
            <div className="review-header">
              <div className="reviewer-info">
                {review.reviewer_image ? (
                  <img
                    src={`/uploads/thumbnails/${review.reviewer_image}`}
                    alt={review.reviewer_username}
                    className="reviewer-avatar"
                    onError={(e) => { if (e.target.src.includes('/thumbnails/')) e.target.src = `/uploads/${review.reviewer_image}`; }}
                  />
                ) : (
                  <div className="reviewer-avatar-placeholder">
                    {review.reviewer_username.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="reviewer-details">
                  <span className="reviewer-name">{review.reviewer_username}</span>
                  <span className="review-date">{formatDate(review.created_at)}</span>
                </div>
              </div>
              <StarRating rating={review.rating} size="small" />
            </div>

            {review.comment && (
              <p className="review-comment">{review.comment}</p>
            )}

            {review.product_title && (
              <div className="review-product">
                {review.product_image && (
                  <img src={`/uploads/thumbnails/${review.product_image}`} alt="" onError={(e) => { if (e.target.src.includes('/thumbnails/')) e.target.src = `/uploads/${review.product_image}`; }} />
                )}
                <span>{review.product_title}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="reviews-pagination">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            {t('common.previous')}
          </button>
          <span>{t('common.page', { current: page, total: pagination.totalPages })}</span>
          <button
            disabled={page === pagination.totalPages}
            onClick={() => setPage(page + 1)}
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SellerReviews;
